import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { userQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import { GetUsersResponse } from "@/types/response/GetUsersResponse";

type UseUsersParams = {
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  tenantId?: string | null;
  clientId?: string | null;
};

/**
 * Hook to fetch a paginated list of users with filtering, sorting, and search capabilities.
 *
 * @param params - Query parameters for filtering and pagination
 * @param params.descending - Whether to sort in descending order
 * @param params.orderBy - Column name to sort by
 * @param params.page - Page number for pagination
 * @param params.pageSize - Number of items per page
 * @param params.search - Search query string
 * @param params.tenantId - Tenant ID to filter users by
 * @param params.clientId - Client ID to filter users by
 *
 * @returns React Query result containing users data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data: users, isLoading } = useUsers({
 *   page: 1,
 *   pageSize: 10,
 *   search: "john",
 *   tenantId: "123"
 * });
 * ```
 */
export function useUsers(params: UseUsersParams) {
  return useQuery<GetUsersResponse, AxiosError>({
    queryKey: userQueryKeys.list({
      descending: params.descending,
      orderBy: params.orderBy,
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      tenantId: params.tenantId,
      clientId: params.clientId,
    }),
    queryFn: () =>
      instance
        .get(`/api/v2/Users`, {
          params: {
            descending: params.descending,
            orderBy: params.orderBy,
            page: params.page,
            pageSize: params.pageSize,
            search: params.search,
            tenantId: params.tenantId || undefined,
            clientId: params.clientId || undefined,
          },
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
  });
}
